import { Component, OnInit, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { LoginService } from '../../services/login.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { UserDataService } from 'src/app/services/core/user-data.service';
import { PEDGE_ID_ROLE_LIST } from 'src/app/shared/constants/roles.constants';
import { ForgotPassDialog } from './forgot-pass-dialog/forgot-pass-dialog';
import { environment } from 'src/environments/environment';
import { HttpService } from 'src/app/services/core/http.service';
import { DeactivatedAccountComponent } from 'src/app/pages/login/deactivate-dialog/deactivate-dialog.component';
import { AmplitudeService } from 'src/app/services/core/amplitude.service';
import { ActivatedRoute, NavigationCancel, NavigationError, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ICredentialsLoginResponse } from 'src/app/shared/interfaces/ICredentialsLoginResponse';
import { LoggingService } from 'src/app/services/core/logging.service';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel, MatInput, MatSuffix, MatPrefix } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MobileAppPromptService } from './mobile-app-dialog/mobile-app-prompt.service';
import { PanteraRunnerComponent } from 'src/app/shared/components/pantera-runner/pantera-runner.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIcon,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatCheckbox,
    MatButton,
    MatPrefix,
    TranslateModule,
    PanteraRunnerComponent,
  ],
})
export class LoginComponent implements OnInit {
  private dialog = inject(MatDialog);
  translate = inject(TranslateService);
  loginService = inject(LoginService);
  user = inject(UserDataService);
  private http = inject(HttpService);
  private amplitudeService = inject(AmplitudeService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private logging = inject(LoggingService);
  private mobileAppPrompt = inject(MobileAppPromptService);

  public username: string;
  public userpass: string;
  public rememberMe = true;

  public showErrorMessage = false;
  public showSuccessMessage = false;
  public isLoading = false;
  public feedbackMessage: { type: 'error' | 'success' | null; key: string } = {
    type: null,
    key: '',
  };
  public showPass = false;

  public version = `${environment.version}`;
  public isDev = !environment.production;

  public isMfaRequired = false;
  public mfaCode: string;
  public hiddenEmail: string;
  public countdown = signal<string | undefined>(undefined);
  public countDownCheck = signal<boolean | undefined>(undefined);

  constructor() {
    // O sucesso mantém o loader ligado até a navegação para o mapa; se ela não acontecer, libera o formulário.
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationCancel || e instanceof NavigationError),
        takeUntilDestroyed()
      )
      .subscribe(() => (this.isLoading = false));
  }

  ngOnInit(): void {
    this.loginService.signOut();
    this.translate.use(this.user.getLang());
    this.openResetParam();
    this.mobileAppPrompt.prompt();
  }

  openResetParam() {
    this.route.queryParams.subscribe(async (params) => {
      const resetSuccess = params['reset_success'];

      if (resetSuccess === 'true') {
        setTimeout(() => {
          this.snackBar.open(
            this.translate.instant('MAP.PASSWORD_RESETED'),
            this.translate.instant('MAP.CLOSE'),
            { duration: 5000, panelClass: 'snackbar-true' }
          );
        }, 200);
      } else if (resetSuccess === 'false') {
        setTimeout(() => {
          this.snackBar.open(
            this.translate.instant('MAP.INVALID_EXPIRED_LINK'),
            this.translate.instant('MAP.CLOSE'),
            { duration: 5000, panelClass: 'snackbar-false' }
          );
        }, 200);
      }

      // Maestro redireciona pra /login?sso_error=true quando o SAMLResponse
      // não valida ou o usuário não tem conta cadastrada (ver /v2/saml/acs).
      if (params['sso_error'] === 'true') {
        setTimeout(() => {
          this.snackBar.open(
            this.translate.instant('MAP.SSO_LOGIN_FAILED'),
            this.translate.instant('MAP.CLOSE'),
            { duration: 5000, panelClass: 'snackbar-false' }
          );
        }, 200);
      }
    });
  }
  async sign_in() {
    this.isLoading = true;
    this.clearFeedback();
    let navigating = false;

    try {
      this.userpass = this.userpass.replace(/\s/g, '');
      this.username = this.username.replace(/\s/g, '');

      const response: ICredentialsLoginResponse = await this.loginService.credentialsLogin(
        this.username,
        this.userpass,
        this.rememberMe
      );

      if (!response.success) {
        if (response.error === 'Falha na rede ou comunicação com o servidor') {
          this.feedbackMessage = { type: 'error', key: 'MAP.SERVER_ERROR' };
        } else {
          this.feedbackMessage = { type: 'error', key: 'MAP.INVALID_CREDENTIALS' };
        }
        return;
      }

      // caso retorne a utilizar, tem que refatorar ela
      if (response.isMfaRequired) {
        this.logging.logInfo('-- MFA Required');
        this.clearMFAStatesAfterAuthSuccess(response['hidden_email']);
        return;
      }

      this.feedbackMessage = { type: 'success', key: 'MAP.LOGGED_SUCCESSFULLY' };
      await this.loginService.tokenLogin(response.jwt);

      const auth_user_data = await this.http.maestroUserGet(`v2/pweb_auth`);

      const isOperatorOrSupportP2 = this.isAllowedToOpenCamerasInterface(auth_user_data.id_role);
      this.loginService.setIsOperatorOrSupportP2(isOperatorOrSupportP2);

      this.amplitudeService.sendEvent('Fez Login', {
        Username: this.username,
        'ID Planta': auth_user_data.id_planta,
      });

      if (!auth_user_data.status_planta && auth_user_data.id_cliente === 2)
        return this.showBlockedAccountModal();

      navigating = true;
    } catch (error) {
      this.logging.logError('LoginComponent.sign_in', error);
      this.feedbackMessage = { type: 'error', key: 'MAP.GENERIC_ERROR' };
    } finally {
      if (!navigating) this.isLoading = false;
    }
  }

  clearMFAStatesAfterAuthSuccess(hiddenEmail: any) {
    this.showErrorMessage = false;
    this.showSuccessMessage = false;
    this.isMfaRequired = true;
    this.hiddenEmail = hiddenEmail;
    this.loginService.setMfaExpired(false);
    this.mfaCode = '';
    this.startCountdown(90);
    this.countDownCheck.set(true);
  }

  isAllowedToOpenCamerasInterface(idRole: number) {
    return PEDGE_ID_ROLE_LIST.includes(idRole);
  }

  async submitMfaCode() {
    this.isLoading = true;
    let navigating = false;

    try {
      const success = await this.loginService.mfaLogin(
        this.username,
        this.userpass,
        this.rememberMe,
        this.mfaCode
      );
      if (!success) {
        this.logging.logError('LoginComponent.submitMfaCode');
        return;
      }

      const auth_user_data = await this.http.maestroUserGet(`v2/pweb_auth`);

      const isOperatorOrSupportP2 = this.isAllowedToOpenCamerasInterface(auth_user_data.id_role);
      this.loginService.setIsOperatorOrSupportP2(isOperatorOrSupportP2);

      if (!auth_user_data.status_planta && auth_user_data.id_cliente === 2) {
        return this.showBlockedAccountModal();
      }
      this.loginService.setWrongMfa(false);
      this.amplitudeService.sendEvent('Fez Login', {
        Username: this.username,
        'ID Planta': auth_user_data.id_planta,
      });
      navigating = true;
    } catch (error) {
      this.logging.logError('LoginComponent.submitMfaCode', error);
      this.feedbackMessage = { type: 'error', key: 'MAP.GENERIC_ERROR' };
    } finally {
      if (!navigating) this.isLoading = false;
    }
  }

  async resendMfaCode() {
    const mfaData = {
      username: this.username.trim(),
      userpass: this.userpass.trim(),
      extended: this.rememberMe,
      request_new_code: true,
    };

    const response = await this.http.maestroUnauthPost('v2/pweb_login', mfaData);

    if (response['mfa'] === true) {
      this.hiddenEmail = response['hidden_email'];
      this.startCountdown(90);
      this.countDownCheck.set(true);
      this.loginService.setMfaExpired(false);
      this.loginService.setWrongMfa(false);
      this.mfaCode = '';
    }
  }

  startCountdown(duration: number) {
    let timer = duration,
      minutes: number,
      seconds: number;

    const countdownInterval = setInterval(() => {
      minutes = Math.floor(timer / 60);
      seconds = timer % 60;

      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

      const timeRemaining = `${formattedMinutes}:${formattedSeconds}`;
      this.countdown.set(timeRemaining);

      if (--timer < 0) {
        clearInterval(countdownInterval);
        this.countdown.set('');
        this.countDownCheck.set(false);
      }
    }, 1000);
  }

  login_sso() {
    // Redirect de pagina inteira, nao navegacao do Angular Router: o SAML
    // SP-initiated comeca no Maestro (/v2/saml/login monta o AuthnRequest e
    // manda pra Atvos). Ver brainstorm-sso-microsoft-atvos.md §3.10.
    window.location.href = `${environment.api}/v2/saml/login`;
  }

  forgot_pass() {
    this.dialog.open(ForgotPassDialog, {
      data: {},
    });
  }

  switchLang(lang: string) {
    this.translate.use(lang);
  }

  showBlockedAccountModal() {
    this.dialog.open(DeactivatedAccountComponent, {
      width: '500px',
    });
  }

  private clearFeedback(): void {
    this.feedbackMessage = { type: null, key: '' };
  }
}
